import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { DepositBloc } from '@connect-services/client/blocs/deposit.bloc';
import {
    CreateDepositLayerModalComponent,
    GeometryType,
} from '@connect-services/client/components/modals/deposit/create-deposit-layer-modal/create-deposit-layer-modal.component';
import { RouteCompletePathEnum } from '@connect-services/client/models/routes.model';
import { ColumnPropertiesDto, Deposit, DepositsService, TablesPropertiesDto } from '@connect-services/client/sdk/api';
import {
    ColumnType,
    CreateDepositLayerService,
} from '@connect-services/client/services/deposit/layer/create-deposit-layer.service';
import { DetailPanelService, PanelAction } from '@connect-services/client/services/detail-panel.service';
import { DocumentService } from '@connect-services/client/services/document.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { ExpansionPanelComponent } from '@connect-services/src/lib/components/expansion-panel/expansion-panel.component';
import { InputTextComponent } from '@connect-services/src/lib/components/input/text/input-text/input-text.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { FormGroupType } from '@shared/client/models/form.model';
import { FormService } from '@shared/client/services/form.service';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

interface DepositEditionFormType extends FormGroupType {
    name: FormControl<string>;
}

type CreateLayerModalResult = {
    columnProperties: Array<{
        length: number | null;
        name: string;
        required: boolean;
        type: ColumnType;
    }>;
    geometryType: GeometryType;
    name: string;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, ExpansionPanelComponent, InputTextComponent, LoadingComponent, RessourcesModule],
    selector: 'app-deposit-edition',
    templateUrl: './deposit-edition.component.html',
})
export class DepositEditionComponent implements OnInit {
    public readonly deposit = signal<Deposit | undefined>(undefined);
    public formGroup!: FormGroup<DepositEditionFormType>;

    protected readonly ButtonType = ButtonType;
    protected readonly createDepositLayerService = inject(CreateDepositLayerService);
    protected readonly documentService = inject(DocumentService);

    private depositBloc = inject(DepositBloc);
    private readonly depositsService = inject(DepositsService);
    private destroyRef = inject(DestroyRef);
    private readonly detailPanelService = inject(DetailPanelService);
    private readonly matDialog = inject(MatDialog);
    private readonly snackBarService = inject(SnackBarService);
    private readonly translocoService = inject(TranslocoService);

    public constructor(private location: Location) {
        this.destroyRef.onDestroy(() => {
            this.detailPanelService.clear();
        });

        effect(() => {
            if (!this.depositBloc.value()) {
                return;
            }

            this.deposit.set(this.depositBloc.value()!);
        });

        this.detailPanelService.title.set(this.translocoService.translate('updateDeposit'));
        this.detailPanelService.navigateTo.set(RouteCompletePathEnum.ORGANIZATION_DATA);
    }

    public ngOnInit(): void {
        this.formGroup = this.initForm();
        this.detailPanelService.actions.set(this.getPanelActions());
    }

    protected openAddLayerModal(): void {
        const dialogRef = this.matDialog.open(CreateDepositLayerModalComponent, {
            autoFocus: false,
            data: {},
            width: '45vw',
        });

        dialogRef.afterClosed().subscribe((response: CreateLayerModalResult | null): void => {
            if (!response) {
                return;
            }

            const newTable: TablesPropertiesDto = {
                columnProperties: response.columnProperties.map((column): ColumnPropertiesDto => ({
                    length: column.length ?? undefined,
                    name: column.name,
                    required: column.required,
                    type: column.type,
                })),
                geometryType: response.geometryType,
                name: response.name,
            };

            this.createDepositLayerService.newCreatedLayers.set([
                ...this.createDepositLayerService.newCreatedLayers(),
                newTable,
            ]);
        });
    }

    private getPanelActions(): PanelAction[] {
        return [
            new PanelAction({
                isActive: true,
                label: this.translocoService.translate('cancel'),
                onClick: (): void => {
                    this.createDepositLayerService.newCreatedLayers.set([]);
                    this.location.back();
                },
                type: ButtonType.SECONDARY,
            }),
            new PanelAction({
                icon: ['fas', 'check'],
                isActive: this.formGroup.valid,
                label: this.translocoService.translate('submit'),
                onClick: async (): Promise<void> => {
                    await this.updateDeposit();
                    this.location.back();
                },
            }),
        ];
    }

    private initForm(): FormGroup<DepositEditionFormType> {
        return new FormGroup<DepositEditionFormType>({
            name: new FormControl<string>(this.depositBloc.value()!.name, {
                nonNullable: true,
                validators: [
                    Validators.maxLength(FormService.VARCHAR_SMALL_LENGTH),
                    Validators.minLength(FormService.VARCHAR_MIN_LENGTH),
                    Validators.required,
                ],
            }),
        });
    }

    private async updateDeposit(): Promise<void> {
        if (!this.deposit()) {
            return;
        }

        try {
            await firstValueFrom(
                this.depositsService.depositControllerAddTables({
                    createTablesDto: { tablesProperties: this.createDepositLayerService.newCreatedLayers() },
                    id: this.deposit()!.id,
                }),
            );
        } catch {
            this.snackBarService.openErrorSnackBar('errorCreatingLayer');
        } finally {
            this.createDepositLayerService.newCreatedLayers.set([]);
        }
    }
}
